import React from 'react';
import { Box, Typography, LinearProgress, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Werteverteilung eines Parameters (#215).
 *
 * Zeigt die Textwerte mit Anzahl und Anteil -- die Form, die
 * `profile_bundle` 1.2 als `value_distribution` liefert:
 * `{value, count, percentage}`, absteigend sortiert, der Sammeleintrag am Ende.
 *
 * **Die Reihenfolge kommt vom Erzeuger und wird hier nicht angetastet.** Der
 * Sammeleintrag ist kein Wert, sondern der Rest; neu sortiert stuende er bei
 * vielen seltenen Werten vorn und laese sich als haeufigster Wert.
 *
 * Gibt es keine Liste, aber eine Begruendung (Freitext oberhalb der Obergrenze,
 * fehlende Textspalte, Unterdrueckung), steht die Begruendung da. Eine leere
 * Karte waere die schlechtere Auskunft.
 */
const WerteverteilungCard = ({ verteilung, grund, kompakt = false, maxEintraege = 3 }) => {
  const { t } = useTranslation();
  const alle = Array.isArray(verteilung) ? verteilung : [];

  if (alle.length === 0 && !grund) {
    return null;
  }

  // **Kurzform fuer den aufgeklappten Bereich (#244).** Der aufgeklappte Bereich
  // ist eine Triage-Flaeche: man scrollt durch 3900 Zeilen und klappt eine auf,
  // um zu entscheiden, ob es der gesuchte Parameter ist. Dafuer genuegen die
  // haeufigsten Werte -- die Verteilungen reichen bis 25 Eintraege, und eine
  // Liste dieser Laenge verdraengt alles andere aus der Zeile.
  //
  // Gekuerzt wird **nur die Anzeige**, nicht die Aussage: die Zahl der
  // ausgelassenen Eintraege steht darunter, damit die Kurzform nicht wie eine
  // vollstaendige Liste aussieht. Genau diese Verwechslung verbietet der
  // Erzeuger sich selbst (`_werte_verteilung`: "eine auf 50 gekuerzte Liste
  // saehe aus wie eine vollstaendige und waere eine Luege ueber die Daten").
  const eintraege = kompakt ? alle.slice(0, maxEintraege) : alle;
  const ausgelassen = alle.length - eintraege.length;

  const sammelLabel = t('dataCatalog.expanded.valueDistributionOther');
  const istSammeleintrag = (wert) => wert === 'sonstige' || wert === sammelLabel;

  return (
    <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, boxShadow: 1 }}>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        sx={{ mb: 1, color: 'text.secondary' }}
      >
        {t('dataCatalog.expanded.valueDistribution')}
      </Typography>

      {eintraege.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {grund}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {eintraege.map((eintrag, index) => {
            const sammel = istSammeleintrag(eintrag.value);
            const anteil = Number.isFinite(eintrag.percentage) ? eintrag.percentage : 0;
            return (
              <Box
                key={`${eintrag.value ?? 'null'}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Tooltip title={eintrag.value ?? ''}>
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 160,
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                      fontSize: '0.82rem',
                      fontStyle: eintrag.value == null || sammel ? 'italic' : 'normal',
                      color: eintrag.value == null || sammel ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {eintrag.value == null
                      ? t('dataCatalog.expanded.valueDistributionEmpty')
                      : sammel
                        ? sammelLabel
                        : eintrag.value}
                  </Typography>
                </Tooltip>

                <Box sx={{ flex: 1, minWidth: 60 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, anteil))}
                    color={sammel ? 'inherit' : index === 0 ? 'primary' : 'secondary'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {Number.isFinite(eintrag.count) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 90, textAlign: 'right' }}
                  >
                    {t('dataCatalog.expanded.valueDistributionCount', { count: eintrag.count })}
                  </Typography>
                )}

                <Chip
                  label={`${anteil.toFixed(1)} %`}
                  size="small"
                  color={sammel ? 'default' : index === 0 ? 'primary' : 'default'}
                  sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 58 }}
                />
              </Box>
            );
          })}

          {ausgelassen > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('dataCatalog.expanded.valueDistributionMore', { count: ausgelassen })}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WerteverteilungCard;
